/**
 * Global music mute + volume — fixed corner, doesn't cover primary UI.
 * Persists to poker_settings (musicEnabled / musicVolume).
 */
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  applyMusicSettings,
  getMusicUiState,
  playIntroStinger,
  sceneFromPath,
  setBgmScene,
  setMusicVolumePercent,
  toggleMusicMute,
  unlockPokerBgm,
} from "@/lib/poker-bgm";
import { unlockCasinoAudio } from "@/lib/casino-sfx";
import { Volume2, VolumeX } from "lucide-react";

export function AudioDock() {
  const [loc] = useLocation();
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(35);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);

  const sync = useCallback(() => {
    const ui = getMusicUiState();
    setMuted(ui.muted);
    setVolume(ui.volume);
  }, []);

  useEffect(() => {
    sync();
    const onChange = () => {
      sync();
      void applyMusicSettings();
    };
    window.addEventListener("poker-settings-changed", onChange);
    return () => window.removeEventListener("poker-settings-changed", onChange);
  }, [sync]);

  // Unlock + intro on first user gesture
  useEffect(() => {
    let playedIntro = false;
    const unlock = () => {
      unlockCasinoAudio();
      unlockPokerBgm();
      if (!playedIntro) {
        playedIntro = true;
        void playIntroStinger().then(() => {
          setReady(true);
          void setBgmScene(sceneFromPath(loc));
        });
      } else {
        setReady(true);
      }
    };
    window.addEventListener("pointerdown", unlock, { once: true, passive: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [loc]);

  // Scene changes with route
  useEffect(() => {
    if (!ready) return;
    void setBgmScene(sceneFromPath(loc));
  }, [loc, ready]);

  const onMute = () => {
    unlockCasinoAudio();
    unlockPokerBgm();
    const next = toggleMusicMute();
    setMuted(next);
    if (!next) void setBgmScene(sceneFromPath(loc));
  };

  const onVol = (v: number) => {
    setVolume(v);
    setMusicVolumePercent(v);
    setMuted(v <= 0);
    unlockPokerBgm();
    if (v > 0) void setBgmScene(sceneFromPath(loc));
  };

  return (
    <div className="audio-dock" data-open={open ? "1" : "0"}>
      {open && (
        <div className="audio-dock__panel" role="group" aria-label="Music volume">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={muted ? 0 : volume}
            onChange={(e) => onVol(Number(e.target.value))}
            className="audio-dock__slider"
            aria-label="Music volume"
          />
          <span className="audio-dock__pct">{muted ? 0 : volume}%</span>
        </div>
      )}
      <button
        type="button"
        className="audio-dock__btn"
        aria-label={muted ? "Unmute music" : "Mute music"}
        aria-pressed={!muted}
        title={muted ? "Music off — click to play" : "Music on — click to mute"}
        onClick={onMute}
        onContextMenu={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>
      <button
        type="button"
        className="audio-dock__gear"
        aria-label="Music volume"
        title="Volume"
        onClick={() => setOpen((o) => !o)}
      >
        ▮
      </button>
    </div>
  );
}
